import { CampaignPausedBanner } from '../../components/CampaignPausedBanner';
import { ReviewReleaseBanner } from './ReviewReleaseBanner';
import { CampaignLimitsBanner } from './CampaignLimitsBanner';
import React from 'react';
import {
  ClaimStatePill,
  queueTaskState,
  totalAvailable,
  totalClaimable,
  type QueueTaskState,
} from './queueParts';
import { Link } from 'react-router-dom';
import { ClipboardCheck, Eye, Loader2, PlayCircle } from 'lucide-react';
import { CampaignSelect } from '../../components/CampaignSelect';
import { CampaignProgressBar } from '../../components/CampaignProgressBar';
import type { CampaignProgressBarVariant } from '../../components/CampaignProgressBar';
import { ClaimSplitButton } from './ClaimSplitButton';
import { Logo } from '../../components/Logo';
import { Tabs } from '../../components/Tabs';
import { UserMenu } from '../../components/UserMenu';
import { useAuth } from '../../contexts/AuthContext';
import { Task } from '../../types';
import type { CampaignStatusCounts } from '../../services/tasks';
import { getTaskTypeDescriptor } from '../../taskTypes/descriptor';
import { TaskIdPill } from '../../components/TaskIdPill';
import { ClaimOption, TaskQueueViewProps, TabId } from './types';
import { ReviewSortControl } from './ReviewSortControl';
import { paths } from '../../routes/paths';
import { EscalatedPill } from './EscalatedPill';

export function DesktopTaskQueueView({
  vm,
  tabs,
  effectiveTab,
  isReviewer,
  canReviewSelected,
  claiming,
  claimNotice,
  taskClaimOptions,
  reviewClaimOptions,
  unassignedReviewCount,
  reviewSortMode,
  onTabChange,
  onReviewSortChange,
  onClaimNext,
  onClaimNextReview,
  onBrowseUnassigned,
}: TaskQueueViewProps) {
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="h-7 w-7 text-primary" />
            <Logo className="text-xl font-bold text-gray-900" />
          </div>
          <UserMenu />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Task Queue</h1>
            <p className="text-sm text-gray-500 mt-1">
              Pick up an available task, return to your work in progress, or review submissions from peers.
            </p>
          </div>
          <CampaignSelect
            campaigns={vm.campaigns}
            selectedId={vm.selectedCampaignId}
            onChange={vm.setSelectedCampaignId}
          />
        </div>

        {vm.selectedCampaign?.paused ? <CampaignPausedBanner /> : <QueueState vm={vm} />}

        {!vm.selectedCampaign?.paused && !vm.busy && !vm.error && (
          <>
            <CampaignLimitsBanner message={vm.campaignLimitsMessage} />
            <Tabs tabs={tabs} activeId={effectiveTab} onChange={(id) => onTabChange(id as TabId)} />
            <div className="mt-6">
              {effectiveTab === 'my' && (
                <div className="space-y-8">
                  <Section
                    icon={<PlayCircle className="h-5 w-5 text-primary" />}
                    title="Tasks"
                    empty="No tasks claimed yet."
                    tasks={vm.buckets.myActive}
                    showReviewDetails
                  >
                    {vm.canClaimInSelected && (
                      <ClaimNextPanel
                        heading="Claim your next task"
                        unitSingular="task"
                        unitPlural="tasks"
                        baseLabel="Claim next task"
                        options={taskClaimOptions}
                        claiming={claiming}
                        notice={claimNotice}
                        onClaim={onClaimNext}
                        statusCounts={vm.campaignStatusCounts}
                        statusVariant="simple"
                      />
                    )}
                  </Section>
                </div>
              )}
              {effectiveTab === 'reviews' && isReviewer && (
                <Section
                  icon={<Eye className="h-5 w-5 text-primary" />}
                  title="Reviews"
                  empty="No reviews in progress."
                  tasks={vm.buckets.myReviews}
                  showTaskId
                  showReviewDetails
                  showEscalation
                  headerAction={
                    <ReviewSortControl
                      value={reviewSortMode}
                      onChange={onReviewSortChange}
                    />
                  }
                >
                  <div className="space-y-3">
                    {canReviewSelected && (
                      <>
                        <ClaimNextPanel
                          heading="Claim your next review"
                          unitSingular="review"
                          unitPlural="reviews"
                          baseLabel="Claim next review"
                          singleLabelSuffix="Review"
                          options={reviewClaimOptions}
                          claiming={claiming}
                          notice={claimNotice}
                          onClaim={onClaimNextReview}
                          browseDisabled={unassignedReviewCount === 0}
                          onBrowse={onBrowseUnassigned}
                          statusCounts={vm.campaignStatusCounts}
                        />
                        <ReviewReleaseBanner campaign={vm.selectedCampaign} />
                      </>
                    )}
                  </div>
                </Section>
              )}
              {effectiveTab === 'rework' && (
                <TaskList
                  tasks={vm.buckets.changesRequested}
                  empty="No revisions waiting on you."
                  showReviewDetails
                />
              )}
              {effectiveTab === 'submitted' && (
                <TaskTable
                  tasks={vm.buckets.submitted}
                  empty="Nothing submitted yet for this campaign."
                  showStatus
                  dateLabel="Submitted"
                  dateValue={(task) => formatSubmittedAt(task)}
                />
              )}
              {effectiveTab === 'approved' && (
                <TaskTable
                  tasks={vm.buckets.approvedInCampaign}
                  empty="No approved tasks in this campaign yet."
                  dateLabel="Approved"
                  dateValue={(task) => (task.reviewedAt ? task.reviewedAt.toLocaleString() : '—')}
                />
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function QueueState({ vm }: { vm: TaskQueueViewProps['vm'] }) {
  if (vm.busy) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (vm.error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
        {vm.error}
      </div>
    );
  }

  if (vm.accessNotice) {
    return (
      <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-sm">
        {vm.accessNotice}
      </div>
    );
  }

  return null;
}

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  empty: string;
  tasks: Task[];
  children?: React.ReactNode;
  showTaskId?: boolean;
  showReviewDetails?: boolean;
  showEscalation?: boolean;
  headerAction?: React.ReactNode;
}

function Section({
  icon,
  title,
  empty,
  tasks,
  children,
  showTaskId = false,
  showReviewDetails = false,
  showEscalation = false,
  headerAction,
}: SectionProps) {
  return (
    <section>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          {icon}
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <span className="text-sm text-gray-400">({tasks.length})</span>
        </div>
        {headerAction}
      </div>
      {children && <div className="mb-3">{children}</div>}
      <TaskList
        tasks={tasks}
        empty={empty}
        showTaskId={showTaskId}
        showReviewDetails={showReviewDetails}
        showEscalation={showEscalation}
      />
    </section>
  );
}

interface TaskListProps {
  tasks: Task[];
  empty: string;
  showTaskId?: boolean;
  showReviewDetails?: boolean;
  showEscalation?: boolean;
}

function TaskList({
  tasks,
  empty,
  showTaskId = false,
  showReviewDetails = false,
  showEscalation = false,
}: TaskListProps) {
  const { isAdmin, isReviewer } = useAuth();
  // Writer name is shown to anyone with review-or-admin standing. Writers
  // looking at their own queue don't need their name reflected back —
  // hiding it keeps the row tight for the common case.
  const canSeeOwner = isAdmin || isReviewer;

  if (tasks.length === 0) {
    return <p className="text-sm text-gray-500">{empty}</p>;
  }

  return (
    <ul className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
      {tasks.map((task) => (
        <li key={task.id}>
          <Link
            to={paths.task(task.id)}
            className="flex items-stretch justify-between gap-4 p-4 hover:bg-gray-50"
          >
            <div className="min-w-0">
              <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
                <p className="font-medium text-gray-900 truncate">{task.title || 'Untitled task'}</p>
                {canSeeOwner && showReviewDetails && (
                  <WriterInline task={task} />
                )}
              </div>
              {task.description && (
                <p className="text-sm text-gray-500 mt-1 line-clamp-2">{task.description}</p>
              )}
              <div className="mt-2 flex items-center gap-2 text-xs text-gray-400">
                <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                  {getTaskTypeDescriptor(task.taskType).displayName}
                </span>
                {showTaskId && <TaskIdPill taskId={task.id} />}
                {showEscalation && <EscalatedPill task={task} />}
                {canSeeOwner && !showReviewDetails && <ReviewRoundPill task={task} />}
                {canSeeOwner && !showReviewDetails && task.ownerName && (
                  <span>writer: {task.ownerName}</span>
                )}
                {task.commentCount > 0 && <span>{task.commentCount} comments</span>}
              </div>
            </div>
            {showReviewDetails ? (
              <div className="flex shrink-0 flex-col items-end justify-between gap-4 text-xs text-gray-400">
                <ReviewRoundPill task={task} />
                <span className="whitespace-nowrap">
                  {formatSubmittedAt(task)}
                </span>
              </div>
            ) : (
              <div className="text-xs text-gray-400 whitespace-nowrap">
                {task.updatedAt.toLocaleString()}
              </div>
            )}
          </Link>
        </li>
      ))}
    </ul>
  );
}

interface TaskTableProps {
  tasks: Task[];
  empty: string;
  /** Submitted-tab only: waiting / under review / approved per row. The
   *  approved tab would just repeat "Approved" on every line. */
  showStatus?: boolean;
  dateLabel: string;
  dateValue: (task: Task) => string;
}

/**
 * Tabular list for the Submitted and Approved tabs. These tabs are read-only
 * ledgers — the reader is scanning down one column ("which of mine is still
 * waiting?", "when was this approved?"), which a card list makes hard. The
 * My Tasks / Rework lists stay as cards: they carry descriptions and are
 * worked one at a time.
 */
function TaskTable({ tasks, empty, showStatus = false, dateLabel, dateValue }: TaskTableProps) {
  const { isAdmin, isReviewer } = useAuth();
  const canSeeOwner = isAdmin || isReviewer;

  if (tasks.length === 0) {
    return <p className="text-sm text-gray-500">{empty}</p>;
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <table className="w-full table-fixed text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <th scope="col" className="px-4 py-2 text-left font-medium">Task</th>
            <th scope="col" className="w-40 px-4 py-2 text-left font-medium">Type</th>
            {canSeeOwner && (
              <th scope="col" className="w-56 px-4 py-2 text-left font-medium">Writer</th>
            )}
            <th scope="col" className="w-20 px-4 py-2 text-left font-medium">Round</th>
            {showStatus && (
              <th scope="col" className="w-40 px-4 py-2 text-left font-medium">Status</th>
            )}
            <th scope="col" className="w-44 px-4 py-2 text-right font-medium">{dateLabel}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {tasks.map((task) => (
            <tr key={task.id} className="hover:bg-gray-50">
              <td className="px-4 py-2">
                <Link
                  to={paths.task(task.id)}
                  className="block truncate font-medium text-gray-900 hover:text-primary"
                  title={task.title || 'Untitled task'}
                >
                  {task.title || 'Untitled task'}
                </Link>
              </td>
              <td className="truncate px-4 py-2 text-gray-600">
                {getTaskTypeDescriptor(task.taskType).shortName}
              </td>
              {canSeeOwner && (
                <td className="truncate px-4 py-2 text-gray-600" title={formatWriter(task) ?? ''}>
                  {formatWriter(task) ?? '—'}
                </td>
              )}
              <td className="px-4 py-2 text-gray-600">{Math.max(1, task.reviewRound)}</td>
              {showStatus && (
                <td className="px-4 py-2 text-xs">
                  <SubmittedStatusPill task={task} />
                </td>
              )}
              <td className="whitespace-nowrap px-4 py-2 text-right text-xs text-gray-500">
                {dateValue(task)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WriterInline({ task }: { task: Task }) {
  const label = formatWriter(task);
  if (!label) return null;
  return <span className="min-w-0 truncate text-xs font-normal text-gray-500">writer: {label}</span>;
}

function formatWriter(task: Task): string | null {
  const name = task.ownerName?.trim() || '';
  const email = task.ownerEmail?.trim() || '';
  const expertId = task.expertId?.trim() || '';
  const nameAndEmail = name && email && name.toLowerCase() !== email.toLowerCase()
    ? `${name} (${email})`
    : name || email;
  return [nameAndEmail, expertId].filter(Boolean).join(' | ') || null;
}

function formatSubmittedAt(task: Task): string {
  return task.submittedAt ? task.submittedAt.toLocaleString() : 'Not submitted';
}

function ReviewRoundPill({ task }: { task: Task }) {
  return (
    <span className="px-2 py-0.5 rounded-full bg-indigo-50 font-medium text-primary">
      Round {Math.max(1, task.reviewRound)}
    </span>
  );
}

function SubmittedStatusPill({ task }: { task: Task }) {
  const { label, tone } = getSubmittedStatusDisplay(task);
  return (
    <span className={`px-2 py-0.5 rounded-full font-medium ${tone}`}>
      {label}
    </span>
  );
}

// DIVERGENCE (deliberate): the submitted queue on desktop has room for
// title-case, spelled-out labels in a background pill; mobile shows the same
// states as short coloured text. What must not diverge is the STATE — that
// comes from the shared `queueTaskState` — so a new status is classified once
// and only its wording is per surface.
const SUBMITTED_PILL: Partial<Record<QueueTaskState, { label: string; tone: string }>> = {
  approved: { label: 'Approved', tone: 'bg-emerald-50 text-emerald-700' },
  under_review: { label: 'Under Review', tone: 'bg-amber-50 text-amber-700' },
};

const SUBMITTED_PILL_FALLBACK = { label: 'Waiting for Review', tone: 'bg-blue-50 text-blue-700' };

function getSubmittedStatusDisplay(task: Task): { label: string; tone: string } {
  return SUBMITTED_PILL[queueTaskState(task)] ?? SUBMITTED_PILL_FALLBACK;
}

interface ClaimNextPanelProps {
  heading: string;
  unitSingular: string;
  unitPlural: string;
  baseLabel: string;
  singleLabelSuffix?: string;
  options: ClaimOption[];
  claiming: boolean;
  notice: string | null;
  onClaim: (option: ClaimOption | null) => void;
  browseDisabled?: boolean;
  onBrowse?: () => void;
  /**
   * Optional campaign-wide status breakdown rendered under the heading.
   * The review panel passes the full breakdown; the task panel passes the
   * two-segment expert view.
   */
  statusCounts?: CampaignStatusCounts | null;
  statusVariant?: CampaignProgressBarVariant;
}

function ClaimNextPanel({
  heading,
  unitSingular,
  unitPlural,
  baseLabel,
  singleLabelSuffix,
  options,
  claiming,
  notice,
  onClaim,
  browseDisabled = false,
  onBrowse,
  statusCounts,
  statusVariant = 'detailed',
}: ClaimNextPanelProps) {
  const available = totalAvailable(options);
  const claimable = totalClaimable(options);
  const unit = available === 1 ? unitSingular : unitPlural;
  const isMulti = options.length > 1;
  const single = !isMulti ? options[0] : null;
  // Always use the generic baseLabel ("Claim next task" / "Claim next
  // review") for the button itself. Type disambiguation lives in the
  // small "Type:" line under the heading on single-type campaigns and
  // in the split-button menu items on multi-type campaigns. Note we
  // ignore singleLabelSuffix for the same reason — it was only used to
  // append qualifiers to the per-type label, which no longer exists.
  void singleLabelSuffix;
  const singleLabel = baseLabel;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-gray-900">{heading}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            <span className="font-semibold text-gray-700">{available}</span> {unit} still available
            {single?.shortName && (
              <span className="ml-2 text-gray-400">·</span>
            )}
            {single?.shortName && (
              <span className="ml-2 text-gray-600">Queue: <span className="font-medium text-gray-800">{single.label}</span></span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {onBrowse && (
            <button
              type="button"
              onClick={onBrowse}
              disabled={browseDisabled || claiming}
              className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
            >
              Browse unassigned
            </button>
          )}
          {isMulti ? (
            <ClaimSplitButton
              baseLabel={baseLabel}
              options={options}
              claiming={claiming}
              onClaim={onClaim}
            />
          ) : (
            <button
              type="button"
              onClick={() => onClaim(single)}
              disabled={claimable === 0 || claiming}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {claiming && <Loader2 className="h-4 w-4 animate-spin" />}
              <span>
                {claiming
                  ? 'Claiming...'
                  : single?.claimingPaused
                    ? 'Claiming Paused'
                    : singleLabel}
              </span>
              {single?.maxReached && !claiming && <ClaimStatePill label="Max reached" />}
            </button>
          )}
        </div>
      </div>
      {statusCounts !== undefined && (
        <CampaignProgressBar
          counts={statusCounts}
          loading={statusCounts === null}
          variant={statusVariant}
        />
      )}
      {notice && <p className="mt-2 text-xs text-gray-500">{notice}</p>}
    </div>
  );
}
